import { EnvironmentMapLoader } from '@/commons/environment/EnvironmentMapLoader';
import { Wedding3DWorld } from '@/littleWedding/Wedding3DWorld';
import { Mesh, NeutralToneMapping, Scene, MeshStandardMaterial, Color, type WebGLRenderer, type Texture } from 'three';

class EnvironmentController {
    getEnvironmentMap: Promise<Texture> | null = null;
    renderer: WebGLRenderer;
    scene: Scene;

    useBackgroundAsEnvironment: boolean = true;
    backgroundColor: Color = new Color(0xdddddd);

    /**
     * Initialize the EnvironmentController singleton. Must be initialized before use!
     */
    initialize(scene: Scene, renderer: WebGLRenderer): void {
        this.scene = scene;
        this.renderer = renderer;
        // NeutralToneMapping is an implementation based on the Khronos 3D Commerce Group standard tone mapping.
        // There are others ways to handle tone mapping, please consult documentation for more information.
        this.renderer.toneMapping = NeutralToneMapping;
        this.renderer.toneMappingExposure = 1.0;
        this.scene.backgroundIntensity = 1.0;
        this.scene.backgroundBlurriness = 1.0;
        this.scene.background = this.backgroundColor;
        this.getEnvironmentMap = EnvironmentController.loadEnvMap();
    }

    async setUseBackgroundAsEnvironment(value: boolean): Promise<void> {
        this.useBackgroundAsEnvironment = value;
        this.scene.background = value ? await this.getEnvironmentMap : this.backgroundColor;
    }

    setBackgroundColor(backgroundColor: Color): void {
        this.backgroundColor = backgroundColor;
        if (!this.useBackgroundAsEnvironment) {
            this.scene.background = this.backgroundColor;
        }
    }

    setToneMappingExposure(toneMappingExposure: number): void {
        this.renderer.toneMappingExposure = toneMappingExposure;
    }

    setBackgroundIntensity(backgroundIntensity: number): void {
        this.scene.backgroundIntensity = backgroundIntensity;
    }

    setBackgroundBlurriness(backgroundBlurriness: number): void {
        this.scene.backgroundBlurriness = backgroundBlurriness;
    }

    /**
     * SetEnvironment base on a string, it will load env and apply. It can work with HDR or LDR (cubemap)
     * If 'none', the background will be set to the default color.
     * @param presetName The name of the environment map preset to use.
     * You can find the available presets in the public/models/environmentMaps folder or in EnvironmentTweakPane.ts
     * eg. 'venice_sunset_1k.hdr', 'meadow_2', 'none'
     */
    setEnvironmentMapPreset(presetName: string): void {
        if (presetName === 'none') {
            this.useBackgroundAsEnvironment = false;
            this.setBackgroundColor(this.backgroundColor);
        } else {
            this.getEnvironmentMap = EnvironmentController.loadEnvMap(presetName);
            this.getEnvironmentMap?.then((envMap: Texture) => {
                if (this.useBackgroundAsEnvironment) {
                    this.scene.background = envMap;
                }
                this.scene.traverse((child) => {
                    if (child instanceof Mesh) {
                        const material = child.material as MeshStandardMaterial;
                        if (material.envMap) {
                            material.envMap = envMap;
                        }
                    }
                });
            });
        }
    }

    static loadEnvMap(presetName: string = 'venice_sunset_1k.hdr'): Promise<Texture> {
        if (presetName.endsWith('.hdr')) {
            return EnvironmentMapLoader.loadEquirectangular(`/models/environmentMaps/${presetName}`, {
                environmentType: 'HDR',
                exposure: 1.0,
            });
        }

        const basePath = `/models/environmentMaps/${presetName}/`;
        const fileNames = ['px', 'nx', 'py', 'ny', 'pz', 'nz'];
        const filePaths = fileNames.map((fileName) => `${basePath + fileName}.png`);

        return EnvironmentMapLoader.loadCube(filePaths, { environmentType: 'LDR', exposure: 1.0 });
    }

    applyEnvmapToMesh(mesh: Mesh): void {
        this.getEnvironmentMap?.then((envMap: Texture) => {
            envMap.colorSpace = Wedding3DWorld.renderer?.outputColorSpace ?? 'srgb';
            mesh.traverse((child) => {
                if (child instanceof Mesh) {
                    const material = child.material as MeshStandardMaterial;
                    material.envMap = envMap;
                }
            });
        });
    }
}

export { EnvironmentController };
